import { useState, useEffect } from "react";
import { 
  Book, 
  ClipboardCheck, 
  FileText, 
  FolderPlus, 
  Loader2, 
  SeparatorHorizontal, 
  Save, 
  ListChecks,
  PlusCircle, 
  FileQuestion 
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { getContractTemplates, saveContractTemplate } from "@/services/contracts/contractService";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ContractSection } from "@/types/contract.types";

interface ContractTemplateLibraryProps {
  msaSections: ContractSection[];
  sowSections: ContractSection[];
  onSelectTemplate: (type: 'msa' | 'sow', sections: string[]) => void;
}

interface TemplateDetailProps {
  templateId: string;
  templateName: string;
  templateType: 'msa' | 'sow';
  templateSections: string[];
  allSections: ContractSection[];
  onApply: () => void;
}

function TemplateDetail({
  templateId,
  templateName,
  templateType,
  templateSections,
  allSections,
  onApply
}: TemplateDetailProps) {
  const typeLabel = templateType === 'msa' ? 'Master Services Agreement' : 'Statement of Work';
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{templateName}</h3>
          <p className="text-sm text-muted-foreground">{typeLabel}</p>
        </div>
        <Badge variant={templateType === 'msa' ? 'outline' : 'secondary'}>
          {templateType.toUpperCase()}
        </Badge>
      </div>
      
      <Separator />
      
      <div>
        <h4 className="text-sm font-medium mb-2">Included Sections:</h4>
        <ScrollArea className="h-[200px] rounded-md border p-4">
          <div className="space-y-2">
            {allSections
              .filter(section => section.category === templateType)
              .sort((a, b) => a.order - b.order)
              .map(section => {
                const isIncluded = templateSections.includes(section.id);
                return (
                  <div key={section.id} className="flex items-start gap-2">
                    <div className={`mt-0.5 ${isIncluded ? 'text-primary' : 'text-muted-foreground'}`}>
                      {isIncluded ? <ClipboardCheck className="h-4 w-4" /> : <FileQuestion className="h-4 w-4" />}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isIncluded ? '' : 'text-muted-foreground'}`}>
                        {section.title}
                      </p>
                      {isIncluded && (
                        <p className="text-xs text-muted-foreground mt-0.5">{section.content.substring(0, 60)}...</p>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </ScrollArea>
      </div>
      
      <div className="flex justify-end">
        <Button onClick={onApply}>
          Apply Template
        </Button>
      </div>
    </div>
  );
}

export function ContractTemplateLibrary({
  msaSections,
  sowSections,
  onSelectTemplate
}: ContractTemplateLibraryProps) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState<'msa' | 'sow'>('msa');
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);
  
  const { 
    data: templates, 
    isLoading: isLoadingTemplates,
    refetch 
  } = useQuery({
    queryKey: ["contract-templates"],
    queryFn: getContractTemplates
  });
  
  useEffect(() => {
    // Initialize with required sections
    if (templateType === 'msa') {
      setSelectedSections(msaSections.filter(s => s.isRequired).map(s => s.id));
    } else {
      setSelectedSections(sowSections.filter(s => s.isRequired).map(s => s.id));
    }
  }, [templateType, msaSections, sowSections]);
  
  const handleTypeChange = (value: string) => {
    setTemplateType(value as 'msa' | 'sow');
    // Reset selected sections based on the new type
    if (value === 'msa') {
      setSelectedSections(msaSections.filter(s => s.isRequired).map(s => s.id));
    } else {
      setSelectedSections(sowSections.filter(s => s.isRequired).map(s => s.id));
    }
  };
  
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    
    try {
      await saveContractTemplate(
        templateName,
        templateType,
        selectedSections
      );
      
      toast.success("Template saved successfully");
      setSaveDialogOpen(false);
      setTemplateName("");
      
      // Refresh templates
      refetch();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };
  
  const handleToggleSection = (sectionId: string) => {
    if (selectedSections.includes(sectionId)) {
      // Check if it's a required section
      const isRequired = templateType === 'msa' 
        ? msaSections.find(s => s.id === sectionId)?.isRequired
        : sowSections.find(s => s.id === sectionId)?.isRequired;
        
      if (isRequired) {
        toast.error("This section is required and cannot be removed");
        return;
      }
      
      setSelectedSections(prev => prev.filter(id => id !== sectionId));
    } else {
      setSelectedSections(prev => [...prev, sectionId]);
    }
  };
  
  const handleApplyTemplate = (template: any) => {
    onSelectTemplate(template.type, template.sections);
    toast.success(`Applied template: ${template.name}`);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Contract Templates</h2>
        
        <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <FolderPlus className="h-4 w-4" />
              Save Current Selection
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save as Template</DialogTitle>
              <DialogDescription>
                Save your current section selections as a reusable template.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  placeholder="e.g., Standard MSA Template"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Template Type</Label>
                <Tabs 
                  defaultValue={templateType} 
                  onValueChange={handleTypeChange}
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="msa">MSA</TabsTrigger>
                    <TabsTrigger value="sow">SOW</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              
              <div className="space-y-2">
                <Label>Template Sections</Label>
                <ScrollArea className="h-60 rounded-md border p-4">
                  <div className="space-y-2">
                    {(templateType === 'msa' ? msaSections : sowSections)
                      .sort((a, b) => a.order - b.order)
                      .map(section => (
                        <div key={section.id} className="flex items-start space-x-2">
                          <div className="flex h-5 items-center space-x-2">
                            <input
                              type="checkbox"
                              id={section.id}
                              checked={selectedSections.includes(section.id)}
                              onChange={() => handleToggleSection(section.id)}
                              disabled={section.isRequired}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          </div>
                          <div className="leading-none">
                            <label
                              htmlFor={section.id}
                              className={`text-sm font-medium ${
                                section.isRequired ? 'text-muted-foreground' : ''
                              }`}
                            >
                              {section.title}
                              {section.isRequired && (
                                <span className="ml-2 text-xs text-muted-foreground">(Required)</span>
                              )}
                            </label>
                          </div>
                        </div>
                      ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveTemplate} className="gap-2">
                <Save className="h-4 w-4" />
                Save Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <Tabs defaultValue="msa">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="msa">MSA Templates</TabsTrigger>
          <TabsTrigger value="sow">SOW Templates</TabsTrigger>
        </TabsList>
        
        <TabsContent value="msa" className="space-y-4 pt-4">
          {isLoadingTemplates ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : templates?.filter(t => t.type === 'msa').length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No MSA Templates</h3>
                <p className="text-sm text-muted-foreground max-w-md mt-2">
                  You haven't created any MSA templates yet. Save your selections as templates to reuse them later.
                </p>
                <Button 
                  onClick={() => setSaveDialogOpen(true)} 
                  variant="outline" 
                  className="mt-4 gap-2"
                >
                  <PlusCircle className="h-4 w-4" />
                  Create Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates
                ?.filter(template => template.type === 'msa')
                .map(template => (
                  <Card 
                    key={template.id} 
                    className={activeTemplate === template.id ? 'border-primary' : ''}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex justify-between items-center">
                        <span className="flex items-center gap-2">
                          <Book className="h-4 w-4" />
                          {template.name}
                        </span>
                        <Badge variant="outline">MSA</Badge>
                      </CardTitle>
                      <CardDescription>
                        {template.sections.length} sections included
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pb-2">
                      <div className="flex flex-wrap gap-1">
                        {msaSections
                          .filter(section => template.sections.includes(section.id))
                          .sort((a, b) => a.order - b.order)
                          .map(section => (
                            <TooltipProvider key={section.id}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="cursor-help">
                                    {section.title.split('.')[1]?.trim()}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{section.title}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ))}
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        onClick={() => {
                          setActiveTemplate(template.id);
                          handleApplyTemplate(template);
                        }}
                        variant="outline" 
                        size="sm" 
                        className="w-full gap-2"
                      >
                        <ListChecks className="h-4 w-4" />
                        Apply Template
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="sow" className="space-y-4 pt-4">
          {isLoadingTemplates ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : templates?.filter(t => t.type === 'sow').length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No SOW Templates</h3>
                <p className="text-sm text-muted-foreground max-w-md mt-2">
                  You haven't created any SOW templates yet. Save your selections as templates to reuse them later.
                </p>
                <Button 
                  onClick={() => {
                    setTemplateType('sow');
                    setSaveDialogOpen(true);
                  }} 
                  variant="outline" 
                  className="mt-4 gap-2"
                >
                  <PlusCircle className="h-4 w-4" />
                  Create Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates
                ?.filter(template => template.type === 'sow')
                .map(template => (
                  <Card 
                    key={template.id} 
                    className={activeTemplate === template.id ? 'border-primary' : ''}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex justify-between items-center">
                        <span className="flex items-center gap-2">
                          <Book className="h-4 w-4" />
                          {template.name}
                        </span>
                        <Badge variant="secondary">SOW</Badge>
                      </CardTitle>
                      <CardDescription>
                        {template.sections.length} sections included
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pb-2">
                      <div className="flex flex-wrap gap-1">
                        {sowSections
                          .filter(section => template.sections.includes(section.id))
                          .sort((a, b) => a.order - b.order)
                          .map(section => (
                            <TooltipProvider key={section.id}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="cursor-help">
                                    {section.title.split('.')[1]?.trim()}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{section.title}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ))}
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        onClick={() => {
                          setActiveTemplate(template.id);
                          handleApplyTemplate(template);
                        }}
                        variant="outline" 
                        size="sm" 
                        className="w-full gap-2"
                      >
                        <ListChecks className="h-4 w-4" />
                        Apply Template
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}